/* eslint-disable camelcase */
import { TRole, TUploadedDocument, TManagingAgency } from './common';
import { TMatchingMode } from './other';
import { TAdditionalService } from '../components/postjob/rolecard/agentview/AgentDelegateCard';
import { constructionTypes, hospitalityTypes, industryTypes, serviceTypes, vehicleTypes } from '../consts';
import { TPossibleDocument } from '../components/freelancer-details/FreelancerDetailsDocuments';


export interface TApiMeta {
    status_code: number;
    status: string;
    // Pagination fields (not present in error responses)
    count?: number;
    next?: string | null;
    previous?: string | null;
    // Error fields (only present in error responses)
    error_code?: string;
    detail?: string;
}

export type TApiResponse<T> = {
    content: T;
    meta: TApiMeta;
};

export type TServiceKey = 'driver' | 'hospitality' | 'industry' | 'construction';

export const serviceSpecificFields = {
    [serviceTypes.CONSTRUCTION]: [
        'role', 'adt', 'aluminium_steel', 'blockwork_facework', 'brickwork', 'builder', 'carpentry', 'cis_rs',
        'civils_operative', 'coded', 'commercial_industrial', 'cpcs_npors', 'cscs', 'domestic', 'dozer',
        'drainage_deep_drainage', 'drawings_literate', 'dry_liner', 'edition_18', 'electrical_mate', 'estimator',
        'excavator_180', 'excavator_360', 'external', 'fabrication', 'first_aid', 'fix_1', 'fix_2', 'foreman',
        'forward_tip_dumper', 'ganger', 'gas_safe', 'gold_black_cscs', 'groundworks', 'hand_tools', 'handballing',
        'housebashing', 'housebashing_line_work', 'internal', 'joiner', 'maintenance', 'mason', 'mewp_ipaf',
        'mig_tig_stick', 'non_cscs', 'painting', 'painting_decorating', 'paper_hanging', 'part_1', 'part_2',
        'patchwork', 'plastering', 'plumbers_mate', 'ppe', 'rams', 'refurb', 'renderer', 'repointing', 'ride_on_roller',
        'roofing', 'shopfitting', 'shuttering_formwork', 'site_supervisor', 'skilled_labourer', 'skimming',
        'slabs_edges_kerbs', 'smsts', 'spraying', 'stone_mason', 'street_works_operative', 'tape_jointing',
        'telehandler', 'testing', 'woodwork',
    ],
    [serviceTypes.DRIVER]: [
        'vehicle_type', 'own_vehicle', 'medium_van', 'large_van', 'minimum_delivery_box',
        'adr', 'hiab',
    ],
    [serviceTypes.HOSPITALITY]: [
        'role', 'supervisor', 'heavy_lifting', 'cocktail_making', 'sous_chef', 'food_preparation',
        'commis_chef', 'chef_de_partie', 'pastry_chef',
    ],
    [serviceTypes.INDUSTRY]: [
        'role', 'forklift_reach', 'forklift_counterbalance',
    ],
    [serviceTypes.OFFICE]: [
        'role', 'legal_secretary', 'solicitors', 'legal_administrator', 'purchasing_manager', 'planner', 'buyer',
        'data_entry', 'assistant', 'sales_administrator', 'operations_assistant', 'head_receptionist', 'inbound_sales',
        'outbound_sales', 'business_development_manager', 'field_sales_manager', 'telesales_representative',
        'sales_executive', 'debit_collector', 'call_centre', 'account_manager', 'customer_service_manager', 'scheduler',
        'sales_support', 'banking', 'retail_assistant', 'manager', 'recruitment_resourcer', 'recruitment_manager',
        'temporaries_consultant', 'permanent_consultant', 'projects', 'it_manager', 'helpdesk_1st_line',
        'helpdesk_2nd_line', 'helpdesk_3rd_line', 'web_designer', 'software_developer', 'it_director',
        'accounts_assistant', 'accountant', 'purchase_ledger', 'sales_ledger', 'payroll', 'bookkeeper',
        'finance_manager', 'quality_assurance', 'audits', 'marketing_assistant', 'marketing_manager',
        'marketing_executive', 'digital_marketing', 'product_manager', 'hr_administration', 'hr_manager',
        'hr_executive', 'hr_consultant', 'office_manager', 'managing_director', 'operation_manager', 'junior',
        'mid_weight', 'regulatory_graphic_designer', 'junior_project_manager', 'project_coordinator',
    ],
};

export type TAddress = {
    id: number;
    address1: string;
    address2: string;
    city: string;
    postcode: string;
    last_used: string;
    url: string;
};

export type TLocation = {
    pathname: string;
    search: string;
    hash: string;
    key?: string;
};

export type THistory = {
    length: number;
    action: string;
    location: TLocation;
};

export type TUploadingFreelancers = {
    importing: boolean;
    imported: boolean;
    payload: any;
};

export type TFreelancerData = {
    emergency_contact_name?: string;
    id: number;
    reference_number: string;
    last_name: string;
    first_name: string;
    email: string;
    mobile: string;
    gender: string;
    birth_date: string;
    service_key: TServiceKey;
    service_role: string;
    referral_channel: any;
    date_joined: string;
    published: boolean;
    date_published: string;
    unpublication_reason: any;
    type_of_job_wanted: string;
    current_occupation: string;
    national_insurance_no: any;
    paye_statement: string;
    sort_code: string;
    account_number: string;
    driving_licence_number: string;
    address1: string;
    address2: string;
    city: string;
    postcode: string;
    passed_presentation: boolean;
    photo_thumbnail_medium: string;
    photo_thumbnail_large: string;
    full_name: string;
    latitude: number;
    longitude: number;
    travel_distance: number;
    referral_code: string;
    experience: number;
    years_experience: number;
    average_score: any;
    last_applied: string;
    is_evicted: boolean;
    eviction_reason: any;
    uploaded_documents: TUploadedDocument[];
    possible_documents: TPossibleDocument[];
    has_valid_documents: boolean;
    strikes_count: number;
    signup_medium: string;
    last_seen: string;
    total_points: number;
    completed_jobs: number;
    agency?: any;
    managing_agency: TManagingAgency;
    connections_count: any;
    is_active: boolean;
    phone_type: string;
    roles?: TFreelancerRole[];
    vehicles?: TVehicleDetails[];
    emergency_contact_phone?: string;
    tempest_payroll_number?: string;
    experience_and_qualifications?: string;
    authorisations_required?: string;
    interview_statements: null | {
        interviewer: string;
        interview_date: string;
        has_signed_contract: boolean;
        identity_confirmed: boolean | null;
    };
    max_weekly_hours: number;
    has_health_problems: boolean;
    health_problems_description: string;
    needs_adjustments: boolean;
    adjustments_description: string;
    has_unspent_convictions: boolean;
    unspent_convictions_description: string;
    is_archived: boolean;
    archived: string | null;
    consent_code: string;
    agent_notes: string;
    availability_lists: number[];
    has_upcoming_booking: boolean;
    is_unsuitable: boolean;
    owning_agencies?: string[];
    share_code_student_restrictions_apply: boolean;
};

export type TFreelancerRole = {
    type: string;
    skills: string[];
};

export type TVehicleDetails = {
    id?: number;
    type?: string;
    active?: boolean;
    own_vehicle: boolean;
    medium_van: boolean;
    large_van: boolean;
    vehicle_type?: number;
    delivery_box?: number;
    vehicle_type_name?: string;
    adr?: boolean;
    hiab?: boolean;
};

export type TRoleDetails = {
    id?: number;
    created?: string;
    type?: string;
    active: boolean;
    own_vehicle?: boolean;
    medium_van?: boolean;
    large_van?: boolean;
    vehicle_type?: number;
    delivery_box?: number;
    vehicle_type_name?: string;
    food_preparation?: boolean;
    cocktail_making?: boolean;
    supervisor?: boolean;
    forklift_counterbalance?: boolean;
    forklift_reach?: boolean;
    sous_chef?: boolean;
    commis_chef?: boolean;
    chef_de_partie?: boolean;
    pastry_chef?: boolean;
    adr?: boolean;
    hiab?: boolean;
};

export type TIndustryDetails = {
    id?: number;
    created?: string;
    type: string;
    active: boolean;
    forklift_counterbalance?: boolean;
    forklift_reach?: boolean;
};

export type TDocument = {
    id: number;
    url: string;
    file: any;
    file2: any;
    type: string;
    subtype: string;
    subtype_description: string;
    account_number: string;
    sort_code: string;
    name: string;
    is_valid: boolean;
    expiry: any;
    is_expired: boolean;
    expires: boolean;
    expires_soon: boolean;
    rejection_reason: any;
    rejection_description: string;
    vehicle: any;
    label: string;
    created: string;
    updated: string;
    reviewed: string;
    freelancer: number;
    zego_policy: any;
    new_document: boolean;
};

export type TDocuments = {
    content: TDocument[];
    loading: boolean;
    hasMoreItems?: boolean;
    meta?: {
        status: string;
        status_code: number;
    };
};

export type TAdminComment = {
    id: number;
    author: number;
    category: number;
    content: string;
    created: string;
    file: any;
}

export type TAdminComments = {
    content: TAdminComment[];
    loading: boolean;
    hasMoreItems?: boolean;
    meta?: {
        status: string;
        status_code: number;
    };
};

export type TDVLACheckSessions = {
    content: TDVLACheckSession[];
    loading: boolean;
    hasMoreItems?: boolean;
    meta?: {
        status: string;
        status_code: number;
    };
}

export type TDVLACheckSession = {
    id: number;
    created: string;
    updated: string;
    detail: string;
    status: string;
    file: string;
}

export type TDVLAConsents = {
    content: TDVLAConsent[];
    loading: boolean;
    hasMoreItems?: boolean;
    meta?: {
        status: string;
        status_code: number;
    };
}

export type TDVLAConsent = {
    id: number;
    created: string;
    freelancer: number;
    file: string;
    consented: boolean;
    expiry: string;
}

export type TRoles = {
    meta?: {
        status_code: number;
        status: string;
    };
    content?: TRoleDetails[] | TVehicleDetails[];
    loading?: boolean;
    hasMoreItems?: boolean;
};

export type TClockInOut = {
    author: string;
    pending: boolean;
    overridden: boolean;
    location: {
        longitude: number;
        latitude: number;
    };
    clocking_time: string;
}

export type TBooking = {
    author: number;
    breaks: number | null;
    can_review: boolean;
    client_feedback: null;
    client_pay_per_hour: number;
    client_total_additional_charges: number;
    client_total_cost: number;
    clock_in: TClockInOut | null;
    clock_out: TClockInOut | null;
    confirmed: boolean | null;
    date_created: string | null;
    datetime_finished: string | null;
    datetime_started: string | null;
    departure: TClockInOut | null;
    eta: string | null;
    eta_updated: string | null;
    first_seen: string | null;
    freelancer: number;
    freelancer_detail: TFreelancerData;
    freelancer_feedback: string | null;
    freelancer_pay_per_hour: number;
    freelancer_total_additional_charges: number;
    freelancer_total_pay: number;
    id: number;
    in_progress: boolean;
    job_title: string;
    last_seen: string | null;
    latest_position: string | null;
    new_booking: string | null;
    old_booking: string | null;
    reference_number: string;
    status: string;
    strikes_count: number;
    // Timesheet-related fields
    timesheet_id?: number | null;
    timesheet_number?: string | null;
    timesheet_status?: string | null;
};

export type TUmbrellaDetails = {
    id: number;
    company_name: string;
    legal_name: string;
    engaged?: boolean;
}

export type TJobItem = {
    id: number;
    service_role: string;
    reference_number: string;
    date: string;
    client_id: number;
    postcode: string;
    start_time: string;
    duration: number;
    client_pay_per_hour: number;
    status: string;
    freelancer_pay_per_hour: number;
    total_hours_booked: number;
    assigned_workers_count: number;
    number_of_freelancers: number;
    freelancers_information: string;
    booking?: TBooking;
    bookings: TBooking[];
    service: TServiceKey;
    additional_services?: TAdditionalService[];
    recurrence_period: number;
    address1: string;
    address2: string;
    city: string;
    longitude: number;
    latitude: number;
    location_map_url: string;
    admin_information: string;
    start_datetime: string;
    end_datetime: string;
    client_final_cost: number;
    client_vat: number;
    commission: number;
    generated_from: any;
    next_date: any;
    client_full: TClientFull;
    max_confirmation_time: any;
    transactions: any;
    series: number;
    series_reference_number: string;
    is_dynamic: boolean;
    dynamic_opt_in: boolean;
    matching_mode: TMatchingMode;
    number_of_backups_required: any;
    number_of_freelancers_to_be_invited: number;
    number_of_invited_freelancers: number;
    number_of_applied_freelancers: number;
    owner_agency_details: TOwnerAgencyDetails;
    managing_agency_details: TManagingAgencyDetails;
    owned: boolean;
    managed: boolean;
    umbrella_details: TUmbrellaDetails;
    is_editable: boolean;
    is_cancelled: boolean;
    occurs_elsewhere: boolean;
    client_checkout_link: any;
    nature_of_business: string;
    required_qualifications: string;
    safety_risks: string;
    available_facilities: string;
    expenses_payable: string;
    title: string;
    vehicle_type?: number;
    medium_van?: boolean;
    large_van?: boolean;
    minimum_delivery_box?: number;
    own_vehicle?: boolean;
    adr?: boolean;
    hiab?: boolean;
    phone_requirement: string;
    role?: string;
    supervisor?: boolean;
    heavy_lifting?: boolean;
    cocktail_making?: boolean;
    sous_chef?: boolean;
    commis_chef?: boolean;
    chef_de_partie?: boolean;
    pastry_chef?: boolean;
    food_preparation?: boolean;
    forklift_reach?: boolean;
    forklift_counterbalance?: boolean;
    // Payroll-specific fields
    consultant_splits?: {
        agent: number;
        agent_name: string;
        consultant_code: string;
        split_value: number;
    }[];
    rates?: {
        id: number;
        jobrequest: number;
        extra_rate: number;
        extra_rate_name: string;
        extra_rate_code: string;
        description: string;
        freelancer_pay_per_hour: string;
        client_pay_per_hour: string;
    }[];
};

export type TClientFull = {
    id: number;
    reference_number: string;
    first_name: string;
    last_name: string;
    company_name: string;
    mobile: any;
    logo: string;
    legal_name: string;
    default_admin_information: string;
    average_score: number;
    default_freelancer_information: string;
    signup_medium: any;
    referral_code?: string;
};

export type TOwnerAgencyDetails = {
    id: number;
    company_name: string;
    contact_phone: string;
    contact_email: string;
    logo: string;
    features: string[];
    strikes_unpublish_threshold: number;
    division?: string;
    department?: string;
};

export type TManagingAgencyDetails = {
    id: number;
    company_name: string;
    contact_phone: string;
    contact_email: string;
    logo: string;
    features: string[];
    strikes_unpublish_threshold: number;
    division?: string;
    department?: string;
};

export type TWorkerItem = {
    id: number;
    reference_number: string;
    last_name: string;
    first_name: string;
    email: string;
    mobile: string;
    gender: string;
    birth_date: string;
    service_key: TServiceKey;
    service_role: string;
    published: boolean;
    date_published: string;
    unpublication_reason: any;
    type_of_job_wanted: any;
    current_occupation: any;
    national_insurance_no: any;
    paye_statement: string;
    address1: string;
    address2: string;
    city: string;
    postcode: string;
    passed_presentation: boolean;
    photo_thumbnail_medium: string;
    photo_thumbnail_large: string;
    full_name: string;
    latitude: number;
    longitude: number;
    travel_distance: number;
    referral_code: string;
    experience: number;
    years_experience: number;
    average_score: any;
    is_evicted: boolean;
    eviction_reason: any;
    uploaded_documents: TUploadedDocument[];
    strikes_count: number;
    signup_medium: string;
    last_seen: string;
    total_points: number;
    completed_jobs: any;
    tempest_payroll_number: string;
    managing_agency: any;
    connections_count: any;
    is_active: boolean;
    roles: TRole[];
};

export type TStoreFragment = {
    loading: TLoading;
    freelancers: TFreelancers;
    shifts: TShifts;
};

export type TLoading = {
    postcode: boolean;
    paygrade: boolean;
    postPayment: boolean;
    clientAddress: boolean;
};

export type TFreelancers = {
    fetching: boolean;
    content: TContent[];
    meta: TMeta;
    selectedFreelancers: any[];
    massBookingResultsError: any;
    massBookingResults: any[];
};

export type TContent = {
    id: number;
    reference_number: string;
    last_name: string;
    first_name: string;
    email: string;
    mobile: any;
    gender: any;
    birth_date: any;
    service_key: string;
    service_role: string;
    published: boolean;
    date_published: any;
    unpublication_reason: string;
    type_of_job_wanted: any;
    current_occupation: any;
    national_insurance_no: any;
    paye_statement: any;
    address1: any;
    address2: any;
    city: any;
    postcode: string;
    passed_presentation: boolean;
    photo_thumbnail_medium: any;
    photo_thumbnail_large: any;
    full_name: string;
    latitude: number;
    longitude: number;
    travel_distance: number;
    referral_code: string;
    experience: number;
    years_experience: number;
    average_score: any;
    is_evicted: boolean;
    eviction_reason: string;
    uploaded_documents: any[];
    strikes_count: number;
    signup_medium: string;
    last_seen: string;
    total_points: number;
    completed_jobs: any;
    tempest_payroll_number: any;
    managing_agency: any;
    connections_count: any;
    is_active: boolean;
    roles: TRole[];
};

export type TMeta = {
    status_code?: number;
    status?: string;
    count: number;
    next?: string;
    previous?: any;
    bookings_count?: number;
};

export type TShifts = {
    fetching: boolean;
    content: any[];
    selections: TSelections;
    meta: TMeta2;
    selectedJobs: any[];
};

export type TSelections = {
    fetching: boolean;
};

export type TMeta2 = {
    count: number;
    previous: any;
    next: any;
};

export type TBookingRequest = {
    service: TServiceKey;
    date: string;
    dates: string[];
    rangeDatesSelected: string[];
    isMultiple: boolean;
    isRemoving: boolean;
    start_time: string;
    duration: number;
    recurrence_period: number;
    number_of_freelancers: number;
    address1: string;
    address2: string;
    city: string;
    postcode: string;
    admin_information: string | null;
    freelancers_information: string | null;
    nature_of_business: string | null;
    required_qualifications: string | null;
    safety_risks: string | null;
    available_facilities: string | null;
    expenses_payable: string | null;
    title: string | null;
    default_nature_of_business: string | null;
    default_required_qualifications: string | null;
    default_safety_risks: string | null;
    default_available_facilities: string;
    default_expenses_payable: string;
    dynamic_opt_in: number;
    voucher: string | null;
    matching_mode: TMatchingMode;
    managed: boolean;
    freelancer_pay_per_hour: number;
    client_pay_per_hour: number;
};

export type TRoleData = {
    driver: {
        vehicle_type: number;
        minimum_delivery_box: number;
        own_vehicle: number;
    };
    hospitality: {
        role: string;
        supervisor: number;
        cocktail_making: number;
        heavy_lifting: number;
        food_preparation: number;
        sous_chef: number;
        commis_chef: number;
        chef_de_partie: number;
        pastry_chef: number;
    };
    industry: {
        role: string;
        forklift_reach: number;
        forklift_counterbalance: number;
    };
};

export type TSeries = {
    id: number;
    series_reference_number: string;
    created: string;
    updated: string;
    client: number;
    client_name: string;
    client_reference_number: string;
    series_description: string;
    series_title: string;
    freelancers: {
        id: number;
        full_name: string;
    }[];
    num_of_confirmed_jobrequests: number;
    total_num_of_jobrequests: number;
    first_job_start: string;
    last_job_start: string;
    client_pay_per_hour: number;
    freelancer_pay_per_hour: number;
    matching_mode: TMatchingMode;
    is_cancelled: boolean;
    is_multiservice: boolean;
    managed: boolean;
    owned: boolean;
    umbrella_details: TUmbrellaDetails;
}

export type TInterview = {
    id: number;
    status: string;
    freelancer_name: string;
    freelancer: number;
    has_valid_documents: boolean;
    role: string;
    score: any
}

export type TInductionSession = {
    id: number;
    author: number;
    title: string;
    service_title: string;
    service: TServiceKey;
    start: string;
    end: string;
    duration: string;
    comment: string;
    slots: number;
    total_booked_freelancers: number;
    total_slots_left: number;
    interviews: TInterview[];
    location: string;
    is_online: boolean;
    extendedProps: {
        eventColor?: string;
  };
}

type TServiceStructureRole = {
    [key: typeof hospitalityTypes[keyof typeof hospitalityTypes]
        | typeof industryTypes[keyof typeof industryTypes]
        | typeof vehicleTypes[keyof typeof vehicleTypes]
        | typeof constructionTypes[keyof typeof constructionTypes]
        ]: any;
};

export type TServicesStructure = {
    [key in TServiceKey]: {
        '': any;
        label: string;
        role_field_name?: 'role' | 'vehicle_type' | never;
        role_filter_name?: 'role' | 'industry_role' | 'vehicle_type' | 'construction_role';
        icon_name?: string;
        role?: TServiceStructureRole;
        industry_role?: TServiceStructureRole;
        vehicle_type?: TServiceStructureRole;
        construction_role?: TServiceStructureRole;
        client_enabled?: boolean;
    };
};

export type TPaymentMethod = {
  id: number;
  status: string;
  is_default: boolean;
  reference_number: string;
  created: string;
  last_used: string;
  url: string;
  bank_name: string;
  country_code: string;
  jobs_count: 2;
  provider: 'banktransfer' | 'stripe';
  description: string;
  removable: boolean;
  customer_id: string;
}
